import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

// Layout choices a page makes for the shell around it. There are two.
//  - `wide`: the main column of the shell is 1080 px wide, which suits forms and reading. A page with a data table asks for more room by
//    calling `useWideLayout()`; the shell then lifts the cap.
//  - `fullBleed`: a workspace that fills the window and scrolls inside its own panes (the concierge workspace) calls
//    `useFullBleedLayout()`; the shell then gives the page the whole height under the top bar, with no padding, no width cap and no page
//    scroll.

type ShellLayout = Readonly<{
  wide: boolean;
  setWide: (wide: boolean) => void;
  fullBleed: boolean;
  setFullBleed: (fullBleed: boolean) => void;
}>;

const ShellLayoutContext = createContext<ShellLayout>({
  wide: false,
  setWide: () => undefined,
  fullBleed: false,
  setFullBleed: () => undefined,
});

export function ShellLayoutProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [wide, setWide] = useState(false);
  const [fullBleed, setFullBleed] = useState(false);
  const value = useMemo(() => ({ wide, setWide, fullBleed, setFullBleed }), [wide, fullBleed]);
  return <ShellLayoutContext value={value}>{children}</ShellLayoutContext>;
}

export function useShellLayout(): ShellLayout {
  return useContext(ShellLayoutContext);
}

/** Asks the shell for the wide main column while the page is mounted (for a data table). */
export function useWideLayout(wide = true): void {
  const { setWide } = useShellLayout();
  useEffect(() => {
    setWide(wide);
    return () => setWide(false);
  }, [wide, setWide]);
}

/**
 * Asks the shell for the whole window under the top bar while the page is mounted (for a workspace with panes that scroll inside
 * themselves). It runs before the first paint, so the page does not jump from the padded column to the full window.
 */
export function useFullBleedLayout(fullBleed = true): void {
  const { setFullBleed } = useShellLayout();
  useLayoutEffect(() => {
    setFullBleed(fullBleed);
    return () => setFullBleed(false);
  }, [fullBleed, setFullBleed]);
}
